// TCP-forward accept-side manager.
//
// When a relay-paired client sends a hello with `mode: "tcp-forward"`,
// `websocket-server.handleHello` hands the connection here instead of through
// `createSessionConnection`. We:
//
//   1. Re-verify the wsToken locally (defence-in-depth — the dialer already
//      passed through the auth-server's `canUserAccessHost` predicate, but the
//      daemon must independently confirm the token is bound to this serverId
//      and this peer pubkey).
//   2. Gate the target: v1 ONLY accepts `127.0.0.1:22`. Anything else → close.
//   3. `net.connect(target.host, target.port)` and pump bytes bidirectionally
//      between the TCP socket and the WS (which is wrapped in the encrypted
//      relay channel — the byte chunks we see have already been decrypted).
//
// Crucially, a tcp-forward connection does NOT allocate a Session, does NOT
// run `sendInitialState`, and does NOT appear in `externalSessionsByKey`. It
// lives in its own `Map<WebSocketLike, TcpForwardConnection>` so the rest of
// the daemon (agent dispatch, tabs, etc.) is completely unaware of it. On
// disconnect, the TCP socket is closed and the entry removed.
//
// Backpressure: when the WS reports `bufferedAmount` over a high-watermark we
// `socket.pause()`. We resume on `drain` (best-effort via the WS bufferedAmount
// polling — Node's `ws` doesn't fire a high-level "drain" event for the
// outgoing side; in v1 we poll every 50 ms while paused).

import net from "node:net";
import type { Logger } from "pino";

import { verifyWsJwt } from "../ws-jwt.js";

// Minimal WS shape we need. Mirrors the private `WebSocketLike` in
// websocket-server.ts — kept local so we don't have to export that type.
export interface WebSocketLike {
  readyState: number;
  bufferedAmount?: number;
  send: (data: string | Uint8Array | ArrayBuffer) => void;
  close: (code?: number, reason?: string) => void;
}

const ALLOWED_TARGET_HOST = "127.0.0.1";
const ALLOWED_TARGET_PORT = 22;

// 64 KB chunk — well under the relay's practical 1 MB per-frame ceiling and
// large enough that scp/rsync throughput isn't bottlenecked by per-chunk
// encryption overhead.
const TCP_READ_CHUNK_SIZE = 64 * 1024;

// Backpressure thresholds on the WS send buffer. Pause TCP reads when the WS
// `bufferedAmount` exceeds HIGH; resume when it drops below LOW.
const WS_BUFFER_HIGH_WATERMARK = 4 * 1024 * 1024; // 4 MB
const WS_BUFFER_LOW_WATERMARK = 1 * 1024 * 1024; // 1 MB
const WS_BUFFER_DRAIN_POLL_MS = 50;

interface TcpForwardConnection {
  ws: WebSocketLike;
  socket: net.Socket;
  logger: Logger;
  ownerUserId: string;
  closed: boolean;
}

export interface TcpForwardAcceptArgs {
  ws: WebSocketLike;
  logger: Logger;
  peerPublicKeyB64: string | null;
  wsToken: string;
  target: { host: string; port: number } | undefined;
  serverPubkeyHex: string | null;
  serverId: string | null;
}

export class TcpForwardManager {
  private readonly connections = new Map<WebSocketLike, TcpForwardConnection>();

  isTcpForward(ws: WebSocketLike): boolean {
    return this.connections.has(ws);
  }

  /**
   * Called from `handleHello` when the hello carries `mode: "tcp-forward"`.
   * Either accepts the connection (registers it, starts the byte pump) or
   * rejects it (logs + closes the WS).
   */
  accept(args: TcpForwardAcceptArgs): void {
    const { ws, logger, peerPublicKeyB64, wsToken, target } = args;
    const log = logger.child({ module: "tcp-forward" });

    if (!args.serverPubkeyHex) {
      log.warn("tcp_forward_rejected_daemon_not_linked");
      this.closeWs(ws, 1008, "auth_required: daemon not linked");
      return;
    }

    const jwt = verifyWsJwt(wsToken, args.serverPubkeyHex, peerPublicKeyB64, args.serverId ?? null);
    if (!jwt.ok) {
      log.warn({ reason: jwt.reason }, "tcp_forward_rejected_invalid_jwt");
      this.closeWs(ws, 1008, `auth_required: ${jwt.reason}`);
      return;
    }

    // v1 gate: target locked to 127.0.0.1:22. Reject anything else hard — we
    // are NOT a generic TCP forwarder.
    if (!target || target.host !== ALLOWED_TARGET_HOST || target.port !== ALLOWED_TARGET_PORT) {
      log.warn({ target }, "tcp_forward_rejected_target_not_allowed");
      this.closeWs(ws, 1008, "target_not_allowed");
      return;
    }

    log.info({ ownerUserId: jwt.userId, target }, "tcp_forward_accept");

    // Open the local TCP socket. SSH on the daemon's machine MUST be bound to
    // loopback (the only address we'll connect to) — but that's an OS-side
    // policy, not something we enforce here.
    const socket = net.connect({ host: target.host, port: target.port });

    const conn: TcpForwardConnection = {
      ws,
      socket,
      logger: log,
      ownerUserId: jwt.userId,
      closed: false,
    };
    this.connections.set(ws, conn);

    this.wirePump(conn);
  }

  /**
   * Called from `handleRawMessage` when a binary frame arrives on a WS that
   * has a tcp-forward registration. Pumps the bytes into the local TCP socket.
   */
  routeBinary(ws: WebSocketLike, chunk: ArrayBuffer): void {
    const conn = this.connections.get(ws);
    if (!conn || conn.closed) return;
    const ok = conn.socket.write(new Uint8Array(chunk));
    // Apply incoming-side backpressure if the local socket can't keep up.
    // (Rare: sshd consumes fast.) We don't have an obvious WS-pause API,
    // so we just rely on TCP's own backpressure here.
    if (!ok) {
      conn.logger.trace("tcp_forward_local_socket_backpressure");
    }
  }

  /**
   * Called from `handleClose` so we can free the TCP socket promptly when the
   * WS closes for any reason.
   */
  handleWsClose(ws: WebSocketLike): boolean {
    const conn = this.connections.get(ws);
    if (!conn) return false;
    this.tearDown(conn);
    return true;
  }

  private wirePump(conn: TcpForwardConnection): void {
    const { socket, ws, logger } = conn;

    // TCP → WS: forward every chunk as a binary frame. Backpressure: pause the
    // socket when WS bufferedAmount exceeds the high-water mark; resume when
    // it drops back below the low-water mark (polled while paused).
    let drainPoll: NodeJS.Timeout | null = null;
    const checkDrain = () => {
      const bufferedAmount = getBufferedAmount(ws);
      if (bufferedAmount === null) return; // can't check; let it through
      if (bufferedAmount < WS_BUFFER_LOW_WATERMARK) {
        if (drainPoll) {
          clearInterval(drainPoll);
          drainPoll = null;
        }
        socket.resume();
      }
    };

    socket.on("data", (chunk: Buffer | string) => {
      if (conn.closed) return;
      // Defensive: socket runs in binary mode (no setEncoding) → `chunk` is a
      // Buffer in practice. Bail loudly if a string ever shows up (would mean
      // someone reconfigured the socket).
      if (typeof chunk === "string") {
        logger.warn("tcp_forward_unexpected_string_chunk");
        this.tearDown(conn);
        return;
      }
      // Cap by TCP_READ_CHUNK_SIZE so a coalesced large burst doesn't form a
      // single mega-frame (the relay caps frames at ~1 MB and the encrypted
      // channel queues fewer than 200 in-flight).
      for (let offset = 0; offset < chunk.length; offset += TCP_READ_CHUNK_SIZE) {
        const sliceBuf = chunk.subarray(offset, offset + TCP_READ_CHUNK_SIZE);
        const slice = new Uint8Array(sliceBuf.buffer, sliceBuf.byteOffset, sliceBuf.byteLength);
        try {
          // The wsLike is wrapped by the encrypted channel on the inbound
          // side, but the OUTBOUND surface is the raw WS — calling
          // `ws.send(ArrayBuffer)` on the encrypted channel's wrapper sends
          // the encrypted ciphertext frame. We invoke through the same path
          // the rest of the daemon uses (`sendBinary` on the WebSocketLike).
          sendBinary(ws, slice);
        } catch (err) {
          logger.warn({ err }, "tcp_forward_send_failed");
          this.tearDown(conn);
          return;
        }
      }

      const bufferedAmount = getBufferedAmount(ws);
      if (bufferedAmount !== null && bufferedAmount > WS_BUFFER_HIGH_WATERMARK) {
        socket.pause();
        if (!drainPoll) {
          drainPoll = setInterval(checkDrain, WS_BUFFER_DRAIN_POLL_MS);
        }
      }
    });

    socket.on("end", () => {
      logger.debug("tcp_forward_socket_end");
      this.tearDown(conn);
    });
    socket.on("error", (err) => {
      logger.warn({ err }, "tcp_forward_socket_error");
      this.tearDown(conn);
    });
    socket.on("close", () => {
      this.tearDown(conn);
    });
  }

  private tearDown(conn: TcpForwardConnection): void {
    if (conn.closed) return;
    conn.closed = true;
    this.connections.delete(conn.ws);
    try {
      conn.socket.destroy();
    } catch {
      // ignore
    }
    try {
      conn.ws.close(1000, "tcp_forward_closed");
    } catch {
      // ignore
    }
  }

  private closeWs(ws: WebSocketLike, code: number, reason: string): void {
    try {
      ws.close(code, reason);
    } catch {
      // ignore
    }
  }
}

// --- Helpers ----------------------------------------------------------------

interface MaybeBufferedAmount {
  bufferedAmount?: number;
}

function getBufferedAmount(ws: WebSocketLike): number | null {
  const v = (ws as unknown as MaybeBufferedAmount).bufferedAmount;
  return typeof v === "number" ? v : null;
}

interface MaybeSend {
  send?: (data: unknown) => unknown;
}

function sendBinary(ws: WebSocketLike, data: Uint8Array): void {
  const sender = (ws as unknown as MaybeSend).send;
  if (typeof sender !== "function") {
    throw new Error("ws has no send method");
  }
  sender.call(ws, data);
}
